import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import PortalAuthLayout from "./PortalAuthLayout";

// Harus sama dengan OTP_COOLDOWN_MS di backend.
const RESEND_COOLDOWN_SECONDS = 60;

const STEPS = ["email", "otp", "password", "done"];

// Satu halaman untuk "buat password" dan "lupa password" — alurnya sama
// persis (email → OTP → password baru), yang berbeda hanya judulnya.
const PortalPassword = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isCreate = params.get("mode") === "create";

  const [step, setStep] = useState("email");
  const [email, setEmail] = useState(params.get("email") || "");
  const [otp, setOtp] = useState("");
  // Hanya disimpan di state: hilang saat halaman di-refresh, dan memang
  // sebaiknya begitu — token ini cukup untuk mengganti password.
  const [resetToken, setResetToken] = useState("");
  const [password, setPassword] = useState("");
  const [confPass, setConfPass] = useState("");
  const [note, setNote] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const fail = (err, fallback) => setNote({ type: "error", text: err.response?.data?.msg || fallback });

  const requestOtp = async (e) => {
    if (e) e.preventDefault();
    if (!email) return setNote({ type: "error", text: "Please input your email." });
    setIsLoading(true);
    try {
      const res = await axios.post("/studentportal/password/otp", { email });
      setNote({ type: "info", text: res.data.msg });
      setOtp("");
      setStep("otp");
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      fail(err, "Unable to send the verification code. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      return setNote({
        type: "error",
        text: `The OTP must be 6 digits — you entered ${otp.length}. Please check the code in your email.`,
      });
    }
    setIsLoading(true);
    try {
      const res = await axios.post("/studentportal/password/verify", { email, otp });
      setResetToken(res.data.resetToken);
      setNote(null);
      setStep("password");
    } catch (err) {
      fail(err, "Unable to verify the code. Please try again.");
      // Kode yang salah dikosongkan supaya bisa langsung diketik ulang.
      setOtp("");
    } finally {
      setIsLoading(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (password.length < 8) return setNote({ type: "error", text: "Password must be at least 8 characters." });
    if (password !== confPass) return setNote({ type: "error", text: "The passwords do not match." });
    setIsLoading(true);
    try {
      const res = await axios.post("/studentportal/password/reset", { email, resetToken, password, confPass });
      setNote({ type: "info", text: res.data.msg });
      setStep("done");
    } catch (err) {
      fail(err, "Unable to save your password. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const subtitle = {
    email: "Enter the email address you registered with. We will send a verification code (OTP) to it.",
    otp: `Enter the 6-digit code sent to ${email}.`,
    password: "Choose a password of at least 8 characters.",
    done: "Your account is ready.",
  }[step];

  return (
    <PortalAuthLayout>
      <h1 className="sp-auth-title">{isCreate ? "Create Password" : "Reset Password"}</h1>
      <p className="sp-auth-subtitle">{subtitle}</p>

      <div className="sp-auth-steps" aria-hidden="true">
        {STEPS.slice(0, 3).map((s, i) => (
          <i key={s} className={STEPS.indexOf(step) >= i ? "is-on" : ""}></i>
        ))}
      </div>

      {note && (
        <div className={`sp-auth-note ${note.type === "error" ? "is-error" : "is-info"}`} role="alert">
          {note.text}
        </div>
      )}

      {step === "email" && (
        <form onSubmit={requestOtp} noValidate>
          <div className="sp-field">
            <label htmlFor="sp-email">Email address</label>
            <input
              id="sp-email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <button type="submit" className="sp-btn" disabled={isLoading}>
            {isLoading ? "Sending..." : "Send verification code"}
          </button>
        </form>
      )}

      {step === "otp" && (
        <form onSubmit={verifyOtp} noValidate>
          <div className="sp-field">
            <label htmlFor="sp-otp">Verification code</label>
            <input
              id="sp-otp"
              className="sp-otp-input"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
            />
          </div>
          <button type="submit" className="sp-btn" disabled={isLoading}>
            {isLoading ? "Verifying..." : "Verify code"}
          </button>
          <p className="sp-auth-foot">
            Did not receive it?{" "}
            <button type="button" className="sp-link" disabled={cooldown > 0 || isLoading} onClick={() => requestOtp()}>
              {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
            </button>
          </p>
        </form>
      )}

      {step === "password" && (
        <form onSubmit={savePassword} noValidate>
          <div className="sp-field">
            <label htmlFor="sp-new-password">New password</label>
            <input
              id="sp-new-password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <div className="sp-field">
            <label htmlFor="sp-conf-password">Confirm new password</label>
            <input
              id="sp-conf-password"
              type="password"
              autoComplete="new-password"
              value={confPass}
              onChange={(e) => setConfPass(e.target.value)}
            />
          </div>
          <button type="submit" className="sp-btn" disabled={isLoading}>
            {isLoading ? "Saving..." : "Save password"}
          </button>
        </form>
      )}

      {step === "done" ? (
        <button type="button" className="sp-btn" onClick={() => navigate("/portal/login")}>
          Go to login
        </button>
      ) : (
        <p className="sp-auth-foot">
          <button type="button" className="sp-link" onClick={() => navigate("/portal/login")}>
            Back to login
          </button>
        </p>
      )}
    </PortalAuthLayout>
  );
};

export default PortalPassword;
